import { memo, useCallback } from 'react';
import { I13nAnchor as I13nAnchorBase } from 'react-i13n';

import { isEnabled } from 'Config';
import { I13N_ACTION_NAVIGATE } from 'Lib/constants/i13nSpec';
import { isSubmitKey } from 'Lib/utils/keyboardUtils';

import { OPEN_IN_NEW_WINDOW } from 'Components/A11y/GlobalDescription';

import type { I13nModel } from '.';

type Props = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  i13nModel?: I13nModel;
};

const I13nAnchor = (props: Props) => {
  const { target, rel, i13nModel, href, onClick } = props;
  const customProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {};
  let normalizedModel = i13nModel;

  const handleClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      e.preventDefault();

      if (onClick) {
        onClick(e);
      }

      if (href) {
        window.open(href);
      }
    },
    [href, onClick]
  );

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLAnchorElement>>(
    (e) => {
      if (isSubmitKey(e.code, false)) {
        // Delegate to click handler for remote view
        if (isEnabled('remote')) {
          handleClick(e as any);
          return;
        }

        if (onClick) {
          onClick(e as any);
        }
        if (!e.isDefaultPrevented()) {
          const windowName = target || '_self';
          const windowFeatures =
            windowName === '_blank' || !rel ? 'noopener,noreferrer' : rel.split(' ').join(',');
          window.open(href, windowName, windowFeatures);
          e.preventDefault();
        }
        e.stopPropagation();
      }
    },
    [onClick, handleClick, href, target, rel]
  );

  if (target && target === '_blank') {
    // security guard
    customProps.rel = 'noopener noreferrer';

    // a11y
    customProps['aria-describedby'] = OPEN_IN_NEW_WINDOW;

    // Remote views are meant to integrate with native clients' webviews.
    // Here, we hijack the I13nAnchor and open the link with window.open instead
    // so that we native clients can intercept the event and open the default browser
    if (isEnabled('remote')) {
      customProps.onClick = handleClick;
    }

    // normalized beacon
    normalizedModel = {
      action: I13N_ACTION_NAVIGATE,
      label: '',
      message: '',
      transport: 'beacon',
      ...i13nModel,
    };
  }

  if (props.role === 'button') {
    customProps.onKeyDown = handleKeyDown;
  }

  return <I13nAnchorBase {...props} {...customProps} i13nModel={normalizedModel} />;
};

I13nAnchor.defaultProps = {
  i13nModel: {},
};

export default memo(I13nAnchor);
